import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, User, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { BACKEND_URL, getErrorMsg } from "../lib/api";

/** Einladung eines Kollegen annehmen: Name + Passwort vergeben, danach automatisch eingeloggt. */
export default function AcceptInvitePage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/colleague-invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setError(getErrorMsg(err, "Einladung ungültig")));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Bitte Ihren Namen eingeben"); return; }
    if (password.length < 8) { toast.error("Passwort muss mindestens 8 Zeichen haben"); return; }
    if (password !== confirm) { toast.error("Passwörter stimmen nicht überein"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/accept-colleague-invite`, {
        token, name: name.trim(), password,
      });
      localStorage.setItem("token", res.data.token);
      toast.success("Willkommen im Eventenergie Portal!");
      // Voller Reload, damit der AuthContext den neuen Benutzer laedt
      window.location.href = "/hub";
    } catch (err) {
      toast.error(getErrorMsg(err, "Registrierung fehlgeschlagen"));
      setSaving(false);
    }
  };

  if (!invite && !error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-fuchsia-600">Einladung wird geprüft...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="accept-invite-page">
      <header className="p-6 flex justify-center">
        <Logo size="large" />
      </header>

      <main className="flex-1 flex items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md">
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-8 space-y-6">
            {error ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Einladung ungültig</h1>
                <p className="text-gray-500">{error}. Bitte lassen Sie sich von Ihrem Kollegen erneut einladen.</p>
              </div>
            ) : (
              <>
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-gray-900">Einladung annehmen</h1>
                  <p className="text-gray-500 mt-1">
                    {invite.invited_by_name ? <><strong>{invite.invited_by_name}</strong> hat Sie eingeladen.</> : "Sie wurden eingeladen."}
                    {" "}Vergeben Sie ein Passwort für <strong>{invite.email}</strong>.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Ihr Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        autoFocus
                        placeholder="Vorname Nachname"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="pl-10 h-12 bg-gray-50 border-gray-300"
                        data-testid="invite-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Passwort</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="password"
                        autoComplete="new-password"
                        placeholder="Mindestens 8 Zeichen"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 h-12 bg-gray-50 border-gray-300"
                        data-testid="invite-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Passwort bestätigen</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="password"
                        autoComplete="new-password"
                        placeholder="Passwort wiederholen"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className="pl-10 h-12 bg-gray-50 border-gray-300"
                        data-testid="invite-confirm-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="w-full h-12 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold"
                    data-testid="invite-submit-btn"
                  >
                    {saving ? "Wird angelegt..." : "Zugang anlegen"}
                  </Button>
                </form>
              </>
            )}

            <Link to="/login" className="flex items-center justify-center gap-2 text-gray-600 hover:text-fuchsia-600 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Zur Anmeldung
            </Link>
          </div>
        </div>
      </main>

      <footer className="p-6 text-center text-sm text-gray-500">
        &copy; {new Date().getFullYear()} Eventenergie Deutschland GmbH & Co. KG
      </footer>
    </div>
  );
}
